import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import BtnTaratic from '../../components/ui/BtnTaratic';
import { GhostSelect } from '../../components/ui/GhostSelect';
import { clinicSchedulesService, type ClinicSchedule } from '../../services/clinicSchedulesService';
import type { ClinicRoom } from '../../services/clinicsService';
import { useNotifications } from '../../components/ui/notifications';

interface ClinicsScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  scheduleToEdit?: ClinicSchedule | null;
  rooms: ClinicRoom[];
}

const DIAS_SEMANA_ES = ['lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado', 'domingo'];

const dayOfWeekFromString = (dia: string): number => {
  const idx = DIAS_SEMANA_ES.indexOf(dia);
  return idx >= 0 ? idx : 0;
};

const dayOfWeekToString = (day: number): string => DIAS_SEMANA_ES[day] || 'lunes';

const ClinicsScheduleModal: React.FC<ClinicsScheduleModalProps> = ({
  isOpen,
  onClose,
  onSave,
  scheduleToEdit,
  rooms
}) => {
  const { t } = useTranslation();
  const notifications = useNotifications();
  const [loading, setLoading] = useState(false);

  const [diaSemana, setDiaSemana] = useState('lunes');
  const [resourceIds, setResourceIds] = useState<number[]>([]);
  
  const [horaApertura, setHoraApertura] = useState('09:00');
  const [horaCierreCitas, setHoraCierreCitas] = useState('17:30');
  const [horaCierre, setHoraCierre] = useState('18:00');
  const [duracion, setDuracion] = useState(30);

  const roomOptions = useMemo(() => [
    ...rooms.map(r => ({ 
      value: String(r.id), 
      label: (
        <div data-ui="clinics-schedule-modal-region-001" className="flex items-center gap-2">
          <div data-ui="clinics-schedule-modal-region-002" className="w-2.5 h-2.5 rounded-full shrink-0 shadow-sm" style={{ backgroundColor: r.color_code || '#808080' }} />
          <span className={!r.is_active ? 'opacity-50 line-through' : ''}>
            {r.name} {!r.is_active && <span className="text-[10px] ml-1">(inactiva)</span>}
          </span>
        </div>
      ),
      textLabel: `${r.name} ${!r.is_active ? '(inactiva)' : ''}`
    }))
  ], [rooms]);

  useEffect(() => {
    if (isOpen && scheduleToEdit) {
      setDiaSemana(dayOfWeekToString(scheduleToEdit.day_of_week));
      setResourceIds([scheduleToEdit.resource_id]);
      
      setHoraApertura(scheduleToEdit.start_time);
      setHoraCierreCitas(scheduleToEdit.end_time);
      setHoraCierre(scheduleToEdit.end_time);
      setDuracion(scheduleToEdit.default_appointment_duration || 30);
    } else if (isOpen) {
      setDiaSemana('lunes');
      setResourceIds(rooms.length > 0 ? [Number(rooms[0].id)] : [1]);
      setHoraApertura('09:00');
      setHoraCierreCitas('17:30');
      setHoraCierre('18:00');
      setDuracion(30);
    }
  }, [isOpen, scheduleToEdit, rooms]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (resourceIds.length === 0) {
        notifications.warning('Revisa los datos', t('clinics.modals.schedule.errors.noRoom'));
        setLoading(false);
        return;
      }

      const dataBase = {
        day_of_week: dayOfWeekFromString(diaSemana),
        start_time: horaApertura,
        end_time: horaCierre,
        default_appointment_duration: duracion
      };

      if (scheduleToEdit) {
        if (resourceIds.includes(scheduleToEdit.resource_id)) {
           await clinicSchedulesService.updateSchedule(scheduleToEdit.id!, { ...dataBase, resource_id: scheduleToEdit.resource_id });
           for (const id of resourceIds.filter(rid => rid !== scheduleToEdit.resource_id)) {
              await clinicSchedulesService.createSchedule({ ...dataBase, resource_id: id });
           }
        } else {
           await clinicSchedulesService.updateSchedule(scheduleToEdit.id!, { ...dataBase, resource_id: resourceIds[0] });
           for (const id of resourceIds.slice(1)) {
              await clinicSchedulesService.createSchedule({ ...dataBase, resource_id: id });
           }
        }
      } else {
        for (const id of resourceIds) {
           await clinicSchedulesService.createSchedule({ ...dataBase, resource_id: id });
        }
      }
      notifications.success(
        scheduleToEdit ? 'Horario actualizado' : 'Horario creado',
        resourceIds.length > 1 ? `Se guardó para ${resourceIds.length} salas.` : 'Se guardó correctamente.',
      );
      onSave();
      onClose();
    } catch (err) {
      console.error('Error saving schedule', err);
      notifications.error('No se pudo guardar el horario', t('clinics.modals.schedule.errors.saveError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-ui="clinics-schedule-modal-region-003" className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div data-ui="clinics-schedule-modal-region-004" className="bg-[#0f172a] rounded-3xl w-full max-w-lg overflow-hidden border border-white/10 shadow-2xl flex flex-col max-h-[90vh]">
        <div data-ui="clinics-schedule-modal-region-005" className="flex justify-between items-center p-6 border-b border-white/10 bg-white/5">
          <h2 className="text-xl font-bold text-white">
            {scheduleToEdit ? t('clinics.modals.schedule.editTitle') : t('clinics.modals.schedule.addTitle')}
          </h2>
          <button onClick={onClose} className="text-white/60 hover:text-white transition-colors">
            <FontAwesomeIcon icon={faTimes} className="text-xl" />
          </button>
        </div>

        <div data-ui="clinics-schedule-modal-region-006" className="p-6 overflow-y-auto">
          <form id="schedule-form" onSubmit={handleSubmit} className="space-y-4">
            
            <div data-ui="clinics-schedule-modal-region-007" className="grid grid-cols-2 gap-4">
              <div data-ui="clinics-schedule-modal-region-008">
                <label className="block text-sm font-medium text-white/80 mb-1">{t('clinics.modals.schedule.dayOfWeek')}</label>
                <GhostSelect
                  value={diaSemana}
                  onChange={(value: string) => setDiaSemana(value)}
                  className="w-full"
                  options={DIAS_SEMANA_ES.map((day) => ({
                    value: day,
                    label: t(`clinics.modals.schedule.days.${day}`),
                  }))}
                />
              </div>

              <div data-ui="clinics-schedule-modal-region-009" className="col-span-2">
                <label className="block text-sm font-medium text-white/80 mb-1">{t('clinics.modals.schedule.rooms')}</label>
                <div data-ui="clinics-schedule-modal-region-010" className="relative z-50">
                  <GhostSelect
                    multiple
                    options={rooms.length === 0 ? [{ value: '1', label: t('clinics.modals.schedule.defaultRoom') }] : roomOptions}
                    value={resourceIds.map(String)}
                    onChange={(val: string[]) => setResourceIds(val.map(Number))}
                    placeholder={t('clinics.modals.schedule.selectRooms')}
                    className="w-full"
                  />
                </div>
              </div>
            </div>

            <div data-ui="clinics-schedule-modal-region-011" className="grid grid-cols-2 gap-4">
              <div data-ui="clinics-schedule-modal-region-012">
                <label className="block text-sm font-medium text-white/80 mb-1">{t('clinics.modals.schedule.openingTime')}</label>
                <input
                  type="time"
                  required
                  value={horaApertura}
                  onChange={(e) => setHoraApertura(e.target.value)}
                  className="w-full bg-black/30 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                />
              </div>

              <div data-ui="clinics-schedule-modal-region-013">
                <label className="block text-sm font-medium text-white/80 mb-1">{t('clinics.modals.schedule.closingTime')}</label>
                <input
                  type="time"
                  required
                  value={horaCierre}
                  onChange={(e) => setHoraCierre(e.target.value)}
                  className="w-full bg-black/30 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                />
              </div>
            </div>

            <div data-ui="clinics-schedule-modal-region-014" className="grid grid-cols-2 gap-4">
              <div data-ui="clinics-schedule-modal-region-015">
                <label className="block text-sm font-medium text-white/80 mb-1">{t('clinics.modals.schedule.lastAppointment')}</label>
                <input
                  type="time"
                  required
                  value={horaCierreCitas}
                  onChange={(e) => setHoraCierreCitas(e.target.value)}
                  className="w-full bg-black/30 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                />
              </div>

              <div data-ui="clinics-schedule-modal-region-016">
                <label className="block text-sm font-medium text-white/80 mb-1">{t('clinics.modals.schedule.slotDuration')}</label>
                <input
                  type="number"
                  min="5"
                  step="5"
                  required
                  value={duracion}
                  onChange={(e) => setDuracion(parseInt(e.target.value) || 30)}
                  className="w-full bg-black/30 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                />
              </div>
            </div>

          </form>
        </div>

        <div data-ui="clinics-schedule-modal-region-017" className="p-6 border-t border-white/10 bg-white/5 flex justify-end gap-3">
          <BtnTaratic color="ghost-dark" onClick={onClose} disabled={loading}>
            {t('clinics.modals.schedule.cancel')}
          </BtnTaratic>
          <BtnTaratic color="green" type="submit" form="schedule-form" disabled={loading}>
            {loading ? t('clinics.modals.schedule.saving') : t('clinics.modals.schedule.save')}
          </BtnTaratic>
        </div>
      </div>
    </div>
  );
};

export default ClinicsScheduleModal;
